import { useCallback, useEffect, useMemo, useState } from "react";
import { usePlaidLink } from "react-plaid-link";
import { toast } from "sonner";
import { api, describeApiError } from "@/lib/api";
import { PlaidImportReview } from "@/components/PlaidImportReview";
import { ArrowsClockwise, ArrowsLeftRight, Bank, CircleNotch, LinkBreak, Plug, Sparkle, UploadSimple } from "@phosphor-icons/react";

const money = (n) => {
  const value = Number(n ?? 0);
  return value.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const stamp = (value) => {
  if (!value) return "Not synced yet";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Not synced yet" : date.toLocaleString();
};

export function PlaidBankSync({ businessId, businessName = "", canEditBooks = true, onTransactionsImported = () => {} }) {
  const [status, setStatus] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);
  const [token, setToken] = useState(null);
  const [openWhenReady, setOpenWhenReady] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [refreshingBalances, setRefreshingBalances] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [reviewSession, setReviewSession] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [inlineNotice, setInlineNotice] = useState("");
  const [transfers, setTransfers] = useState([]);
  const [transfersOpen, setTransfersOpen] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/businesses/${businessId}/plaid/status`);
      setStatus(data);
      if (data.connected) {
        const accountsRes = await api.get(`/businesses/${businessId}/plaid/accounts`);
        setAccounts(accountsRes.data.accounts || []);
      } else {
        setAccounts([]);
      }
      const [sessionRes, transfersRes] = await Promise.all([
        api.get(`/businesses/${businessId}/plaid/review-session`).catch(() => ({ data: { session: null } })),
        api.get(`/businesses/${businessId}/plaid/transfers`).catch(() => ({ data: { transfers: [] } })),
      ]);
      setReviewSession(sessionRes.data.session || null);
      setTransfers(transfersRes.data.transfers || []);
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  const handleSyncResponse = useCallback((data) => {
    if (data?.status === "review_pending") {
      setReviewSession(data.session);
      setDrawerOpen(true);
      setInlineNotice("");
    } else {
      setInlineNotice(data?.rowan_message || "");
      toast.success(data?.rowan_message || "Transactions synced.");
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const onSuccess = useCallback(async (publicToken, metadata) => {
    setLinking(true);
    try {
      await api.post(`/businesses/${businessId}/plaid/exchange`, {
        public_token: publicToken,
        institution_name: metadata?.institution?.name || "Linked bank",
        institution_id: metadata?.institution?.institution_id || "",
      });
      const syncRes = await api.post(`/businesses/${businessId}/plaid/sync`);
      handleSyncResponse(syncRes.data);
      await loadStatus();
      onTransactionsImported();
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setLinking(false);
      setToken(null);
    }
  }, [businessId, loadStatus, onTransactionsImported, handleSyncResponse]);

  const onExit = useCallback((error) => {
    setOpenWhenReady(false);
    setLinking(false);
    if (error?.error_message || error?.display_message) {
      toast.error(error.display_message || error.error_message);
    }
  }, []);

  const { open, ready } = usePlaidLink({ token, onSuccess, onExit });

  useEffect(() => {
    if (openWhenReady && ready) {
      open();
      setOpenWhenReady(false);
      setLinking(false);
    }
  }, [openWhenReady, open, ready]);

  const startLink = async () => {
    setLinking(true);
    try {
      const { data } = await api.post(`/businesses/${businessId}/plaid/link-token`);
      setToken(data.link_token);
      setOpenWhenReady(true);
    } catch (err) {
      toast.error(describeApiError(err));
      setLinking(false);
    }
  };

  const refreshBalances = async () => {
    setRefreshingBalances(true);
    try {
      const { data } = await api.get(`/businesses/${businessId}/plaid/accounts`);
      setAccounts(data.accounts || []);
      const statusRes = await api.get(`/businesses/${businessId}/plaid/status`);
      setStatus(statusRes.data);
      toast.success("Balances refreshed.");
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setRefreshingBalances(false);
    }
  };

  const syncTransactions = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/businesses/${businessId}/plaid/sync`);
      handleSyncResponse(data);
      await loadStatus();
      onTransactionsImported();
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setSyncing(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Plaid from this business? Imported ledger entries will stay in place.")) return;
    setDisconnecting(true);
    try {
      const { data } = await api.delete(`/businesses/${businessId}/plaid/connection`);
      toast.success(data?.rowan_message || "Plaid disconnected.");
      await loadStatus();
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setDisconnecting(false);
    }
  };

  const summary = useMemo(() => ([
    { label: "Institutions", value: status?.institution_count ?? 0, testid: "plaid-summary-institutions" },
    { label: "Accounts", value: status?.account_count ?? 0, testid: "plaid-summary-accounts" },
    { label: "Imported", value: status?.imported_count ?? 0, testid: "plaid-summary-imported" },
  ]), [status]);

  return (
    <div className="bk-rowan-card p-6" data-testid="plaid-status-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-3xl">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-display text-xl font-bold flex items-center gap-2" style={{ color: "var(--bk-text)" }} data-testid="plaid-title">
              <Bank size={20} weight="duotone" color="var(--bk-accent)" /> Bank sync with Plaid
            </h3>
            <span className="inline-flex items-center rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em]" style={{ background: "rgba(91,138,111,0.12)", color: "var(--bk-success)" }} data-testid="plaid-environment-pill">
              {status?.environment || "sandbox"}
            </span>
          </div>
          <p className="text-sm mt-2" style={{ color: "var(--bk-text-2)" }} data-testid="plaid-rowan-note">
            {status?.rowan_message || `Link ${businessName || "this business"} to Plaid and stop babysitting CSV exports.`}
          </p>
          <p className="text-xs mt-2" style={{ color: "var(--bk-text-2)" }} data-testid="plaid-last-sync">
            Last sync: {stamp(status?.last_sync_at)}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button onClick={startLink} disabled={!canEditBooks || loading || linking} data-testid="plaid-connect-button" className="bk-btn-primary inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold disabled:opacity-60">
            {linking ? <><CircleNotch size={16} className="animate-spin" /> Opening Plaid…</> : <><Plug size={16} weight="bold" /> {status?.connected ? "Link another account" : "Connect bank"}</>}
          </button>
          <button onClick={refreshBalances} disabled={!canEditBooks || !status?.connected || refreshingBalances} data-testid="plaid-refresh-balances-button" className="bk-btn-secondary inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold disabled:opacity-60" style={{ color: "var(--bk-text)" }}>
            {refreshingBalances ? <><CircleNotch size={16} className="animate-spin" /> Refreshing…</> : <><ArrowsClockwise size={16} weight="bold" /> Refresh balances</>}
          </button>
          <button onClick={syncTransactions} disabled={!canEditBooks || !status?.connected || syncing} data-testid="plaid-sync-button" className="bk-btn-secondary inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold disabled:opacity-60" style={{ color: "var(--bk-text)" }}>
            {syncing ? <><CircleNotch size={16} className="animate-spin" /> Syncing…</> : <><UploadSimple size={16} weight="bold" /> Import transactions</>}
          </button>
          <button onClick={disconnect} disabled={!canEditBooks || !status?.connected || disconnecting} data-testid="plaid-disconnect-button" className="inline-flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold disabled:opacity-60" style={{ borderColor: "rgba(217,108,74,0.24)", color: "#B23B1F", background: "#fff" }}>
            {disconnecting ? <><CircleNotch size={16} className="animate-spin" /> Disconnecting…</> : <><LinkBreak size={16} weight="bold" /> Disconnect</>}
          </button>
        </div>
      </div>

      {reviewSession && !drawerOpen && (
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          data-testid="plaid-pending-review-banner"
          className="mt-4 w-full text-left rounded-2xl border px-4 py-3 flex items-center gap-3 transition-colors hover:bg-white"
          style={{ borderColor: "rgba(232,168,56,0.4)", background: "var(--bk-rowan-bg)" }}
        >
          <Sparkle size={18} weight="fill" color="var(--bk-gold)" className="shrink-0" />
          <span className="text-sm font-semibold flex-1" style={{ color: "var(--bk-text)" }}>
            You have a pending import — {reviewSession.flagged_count + reviewSession.unflagged_count} transaction{(reviewSession.flagged_count + reviewSession.unflagged_count) !== 1 ? "s" : ""} waiting for review.
          </span>
          <span className="text-xs font-bold shrink-0" style={{ color: "var(--bk-accent)" }}>Resume review →</span>
        </button>
      )}

      {inlineNotice && !reviewSession && (
        <div className="mt-4 rounded-2xl border px-4 py-3 flex items-center gap-2 text-sm" style={{ borderColor: "rgba(91,138,111,0.24)", background: "var(--bk-success-bg)", color: "var(--bk-success)" }} data-testid="plaid-inline-notice">
          <Sparkle size={16} weight="fill" /> {inlineNotice}
        </div>
      )}

      {!canEditBooks && (
        <div className="mt-4 rounded-2xl border px-4 py-3 text-sm" style={{ borderColor: "var(--bk-border)", background: "#fff", color: "var(--bk-text-2)" }} data-testid="plaid-read-only-note">
          Only owners and partners can link or sync bank feeds here.
        </div>
      )}

      {loading ? (
        <div className="mt-5 rounded-2xl border px-4 py-4 text-sm inline-flex items-center gap-2" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text-2)", background: "#fff" }} data-testid="plaid-loading">
          <CircleNotch size={16} className="animate-spin" /> Checking Plaid status…
        </div>
      ) : !status?.configured ? (
        <div className="mt-5 rounded-2xl border px-4 py-4 text-sm" style={{ borderColor: "rgba(217,108,74,0.2)", background: "#fff", color: "var(--bk-text-2)" }} data-testid="plaid-not-configured">
          Plaid isn&apos;t configured on this environment yet.
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {summary.map((item) => (
              <div key={item.label} className="rounded-[20px] border px-4 py-4 bg-white/92" style={{ borderColor: "var(--bk-border)" }} data-testid={item.testid}>
                <p className="text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: "var(--bk-text-2)" }}>{item.label}</p>
                <p className="font-display text-2xl font-bold mt-2" style={{ color: "var(--bk-text)" }}>{item.value}</p>
              </div>
            ))}
          </div>

          {status?.connected ? (
            <div className="rounded-[22px] border bg-white/92 p-4" style={{ borderColor: "var(--bk-border)" }} data-testid="plaid-accounts-panel">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }} data-testid="plaid-accounts-title">Linked accounts</p>
                <span className="text-xs" style={{ color: "var(--bk-text-2)" }} data-testid="plaid-accounts-subtitle">Live balances update here before transactions land in your ledger.</span>
              </div>
              {accounts.length === 0 ? (
                <p className="text-sm" style={{ color: "var(--bk-text-2)" }} data-testid="plaid-accounts-empty">No accounts loaded yet. Try refreshing balances.</p>
              ) : (
                <div className="space-y-2">
                  {accounts.map((account) => (
                    <div key={account.account_id} className="rounded-2xl border px-4 py-3 flex flex-wrap items-center justify-between gap-3" style={{ borderColor: "var(--bk-border)", background: "var(--bk-page)" }} data-testid={`plaid-account-row-${account.account_id}`}>
                      <div>
                        <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }} data-testid={`plaid-account-name-${account.account_id}`}>{account.name}{account.mask ? ` •••• ${account.mask}` : ""}</p>
                        <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }} data-testid={`plaid-account-meta-${account.account_id}`}>{account.institution_name || "Linked bank"} · {account.type || "account"}{account.subtype ? ` / ${account.subtype}` : ""}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs uppercase tracking-[0.14em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Current</p>
                        <p className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }} data-testid={`plaid-account-balance-${account.account_id}`}>{money(account?.balances?.current)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-[22px] border px-4 py-4 text-sm" style={{ borderColor: "var(--bk-border)", background: "#fff", color: "var(--bk-text-2)" }} data-testid="plaid-empty-state">
              Plaid is initialized for this business. Connect a sandbox institution to pull live accounts, balances, and transactions into Books.
            </div>
          )}

          {transfers.length > 0 && (
            <div className="rounded-[22px] border bg-white/92 p-4" style={{ borderColor: "var(--bk-border)" }} data-testid="plaid-transfers-panel">
              <button type="button" onClick={() => setTransfersOpen((v) => !v)} data-testid="plaid-transfers-toggle" className="w-full flex items-center justify-between gap-2">
                <p className="text-sm font-semibold inline-flex items-center gap-2" style={{ color: "var(--bk-text)" }}>
                  <ArrowsLeftRight size={16} weight="bold" color="#2B5EA7" /> Transfers log ({transfers.length})
                </p>
                <span className="text-xs" style={{ color: "var(--bk-text-2)" }}>{transfersOpen ? "Hide" : "Show"} — kept out of your P&L</span>
              </button>
              {transfersOpen && (
                <div className="mt-3 space-y-2">
                  {transfers.map((row) => (
                    <div key={row.plaid_transaction_id} className="rounded-2xl border px-4 py-2.5 flex flex-wrap items-center justify-between gap-2" style={{ borderColor: "var(--bk-border)", background: "var(--bk-page)" }} data-testid={`plaid-transfer-row-${row.plaid_transaction_id}`}>
                      <div>
                        <p className="text-sm font-medium" style={{ color: "var(--bk-text)" }}>{row.description}</p>
                        <p className="text-xs mt-0.5" style={{ color: "var(--bk-text-2)" }}>{row.date} · {row.account || "Bank"}</p>
                      </div>
                      <span className="text-sm font-bold" style={{ color: "#2B5EA7" }}>{row.txn_type === "income" ? "+" : "−"}{money(row.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <PlaidImportReview
        open={drawerOpen}
        session={reviewSession}
        businessId={businessId}
        onClose={() => setDrawerOpen(false)}
        onSessionUpdate={(s) => setReviewSession(s)}
        onFinalized={async () => {
          setDrawerOpen(false);
          setReviewSession(null);
          await loadStatus();
          onTransactionsImported();
        }}
      />
    </div>
  );
}